/**
 * Copyright 2025, IntraLinks, Inc. All rights reserved.
 */

import { getSecretKeyLengthFromUri } from '../../../app/shared/utils/helpers.util';
import { SecuritySetupType } from '../../../domain/models/impl/SecuritySetup';
import { SelectedFactor } from '../setupSecuritySlice';
import { ISecurityFactor } from '../../../domain/models/ISecuritySetup';
import {
  DEFAULT_EMAIL_DIGITS, DEFAULT_MIN_REQUIRED_LOA_LEVEL, DEFAULT_SECRET_KEY_LENGTH, DEFAULT_SMS_DIGITS
} from '../../../app/shared/utils/constants.util';

type FactorDataArgs = { factor: ISecurityFactor };

type FactorDataFn = (a: FactorDataArgs) => SelectedFactor | undefined;

const deriveAuthenticatorData: FactorDataFn = ({ factor }) => {
  const uri = factor.options?.uri ?? '';
  const digits = uri ? getSecretKeyLengthFromUri(uri) : DEFAULT_SECRET_KEY_LENGTH;
  const loaLevel = factor?.level ?? DEFAULT_MIN_REQUIRED_LOA_LEVEL;
  return {
    factorId: factor.id,
    type: factor.type,
    digits,
    loaLevel
  };
};

export const deriveSmsData = ({ factor }: { factor: ISecurityFactor }): SelectedFactor => {
  const loaLevel = factor?.level ?? DEFAULT_MIN_REQUIRED_LOA_LEVEL;
  const phone = factor?.options?.phoneNumber ?? '';

  return {
    factorId: factor.id,
    type: factor.type ?? SecuritySetupType.SMS,
    digits: DEFAULT_SMS_DIGITS,
    loaLevel,
    data: phone
  };
};

export const deriveEmailData = ({ factor }: { factor: ISecurityFactor }): SelectedFactor => {
  const loaLevel = factor?.level ?? DEFAULT_MIN_REQUIRED_LOA_LEVEL;
  const email = factor?.options?.email ?? '';

  return {
    factorId: factor.id,
    type: factor.type,
    digits: DEFAULT_EMAIL_DIGITS,
    loaLevel,
    data: email
  };
};

const VERIFY_FACTOR_DATA_BY_TYPE: Partial<Record<SecuritySetupType, FactorDataFn>> = {
  AUTHENTICATOR: deriveAuthenticatorData,
  SMS: deriveSmsData,
  EMAIL: deriveEmailData
};

export default VERIFY_FACTOR_DATA_BY_TYPE;
